import React from 'react';

const IntroSection = ({ items = [], report_count, actionButtons = [] }) => {
  if (!items.length) return null;

  const {
    heading,
    subheading,
    description,
    onButtonClick,
    onSecondButtonClick,
    image,
  } = items[0];

  // Gradient border style for outline buttons — requires padding-box/border-box trick,
  // not achievable with Tailwind alone
  const outlineStyle = {
    background: '#ffffff',
    border: '1.5px solid var(--color-brand-primary)',
  };

  const languages = [
    { name: 'English', native: null },
    { name: 'Hindi', native: 'हिंदी' },
    { name: 'Odia', native: 'ଓଡ଼ିଆ' },
    { name: 'Bengali', native: 'বাংলা' },
    { name: 'Tamil', native: 'தமிழ்' },
    { name: 'Telugu', native: 'తెలుగు' },
    { name: 'Marathi', native: 'मराठी' },
    { name: 'Kannada', native: 'ಕನ್ನಡ' },
  ];
  const gradientText = {
    fontFamily: "'Inter', sans-serif",
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '25px',
    letterSpacing: '0.03em',
    color: 'var(--color-brand-green)',
  };
  const navyText = { color: 'var(--color-brand-primary)' };
  return (
    <div className="w-full">
      {/* Custom keyframe animations only — cannot be expressed in Tailwind without config changes */}
      <style>{`
        @keyframes glowPulse {
          0%, 100% { box-shadow: 0px 4px 10px 0px #80DAF180; }
          50%       { box-shadow: 0px 4px 18px 2px #80DAF1; }
        }
        @keyframes floatBadge {
          0%, 100% { transform: translateY(0px) scale(1); }
          50%       { transform: translateY(-6px) scale(1.03); }
        }
        .badge-animate {
          animation: glowPulse 3.5s ease-in-out infinite,
                     floatBadge 3.5s ease-in-out infinite;
        }
        .cs-intro-heading {
          color: var(--color-brand-primary);
          font-weight: 700;
        }
        .cs-intro-subheading {
          color: var(--color-brand-green);
          font-weight: 700;
        }
        .cs-intro-desc {
          color: var(--color-text-subheading);
          font-weight: 500;
        }
        .cs-btn-primary {
          color: #ffffff;
          background: var(--color-brand-primary);
          border: 1.5px solid var(--color-brand-primary);
          box-shadow: none;
          transition: background 0.2s;
        }
        .cs-btn-primary:hover { background: var(--color-brand-primary-hover); }
        .cs-btn-outline {
          color: var(--color-brand-primary);
          transition: background 0.2s;
        }
        .cs-btn-outline:hover { background: #EAF3FA !important; }
        .cs-reports-badge {
          background: #ffffff;
          border: 1px solid #80DAF1;
          color: var(--color-brand-primary);
        }
      `}</style>

      {/* ── Wrapper ── */}
      <div
        className="flex flex-col md:flex-row justify-between overflow-hidden px-5 py-6 md:px-8 lg:px-16 xl:px-[max(64px,calc(50%_-_656px))] lg:py-5"
        style={{
          backgroundColor: '#ffffff',
          backgroundImage: 'var(--section-bg-gradient)',
        }}
      >
        {/* ── LEFT ── */}
        <div className="flex flex-col items-center text-center md:items-start md:text-left md:max-w-[50%] md:mt-12">

          {/* Heading */}
          <h1
            className="cs-intro-heading mb-1.5
                       text-[26px] leading-[32px]
                       sm:text-[32px] sm:leading-[38px]
                       lg:text-[52px] lg:leading-[68px]"
          >
            {heading}
          </h1>

          {/* Subheading */}
          <p className="cs-intro-subheading text-base leading-7 sm:text-xl sm:leading-8 lg:text-[30px] lg:leading-[50px]">
            {subheading}
          </p>

          {/* Description */}
          <p className="cs-intro-desc text-base leading-relaxed mb-4 max-w-full md:max-w-[600px]">
            {description}
          </p>

           {/* Language Badge */}
          <div
            className="rounded-xl px-4 py-3 mb-2 max-w-full md:max-w-[600px]"
            style={{
              background: '#FFFFFF',
              border: '1px solid #C9EEF8',
              boxShadow: '0px 4px 10px 0px #80DAF180',
            }}
          >
            <p style={{ margin: 0, ...gradientText }}>
              {/* Figma: labels, English names and bullets in navy; "8 languages" and native scripts in green */}
              <span style={{ ...gradientText, ...navyText, fontWeight: 600 }}>{'Now available in '}</span>
              <span style={{ ...gradientText, fontWeight: 700 }}>{'8 languages: '}</span>
              {languages.map((lang, idx) => (
                <span key={lang.name}>
                  {idx > 0 && <span style={{ ...gradientText, ...navyText, fontWeight: 400 }}>{' • '}</span>}
                  {lang.native ? (
                    <span>
                      <span style={{ ...gradientText, fontWeight: 700 }}>{lang.native}</span>
                      <span style={{ ...gradientText, ...navyText, fontWeight: 400 }}>{` (${lang.name})`}</span>
                    </span>
                  ) : (
                    <span style={{ ...gradientText, ...navyText, fontWeight: 700 }}>{lang.name}</span>
                  )}
                </span>
              ))}
            </p>
          </div>

          {/* ── Action Buttons ── */}
          {actionButtons.length > 0 && (
            <div className="
              flex flex-wrap md:flex-nowrap
              gap-3 mt-5
              items-center
              justify-center md:justify-start
            ">
              {actionButtons.map((btn, idx) => {
                const isSubscribe = btn.variant === 'subscribe';

                const subscribeCls =
                  'cs-btn-primary inline-flex items-center gap-1.5 font-semibold text-[15px] ' +
                  'rounded-full px-6 py-[11px] cursor-pointer whitespace-nowrap ' +
                  'no-underline lg:text-sm lg:px-5 lg:py-2.5 mt-0';

                const outlineCls =
                  'cs-btn-outline inline-flex items-center gap-1.5 font-semibold text-[15px] ' +
                  'rounded-full px-[22px] py-[10px] cursor-pointer whitespace-nowrap ' +
                  'no-underline mt-0 ' +
                  'lg:text-sm lg:px-4 lg:py-2 mt-0';

                const cls = isSubscribe ? subscribeCls : outlineCls;
                const style = isSubscribe ? {} : outlineStyle;

                return btn.href ? (
                  <a
                    key={idx}
                    href={btn.href}
                    target={btn.target || '_self'}
                    rel="noopener noreferrer"
                    className={cls}
                    style={style}
                  >
                    {btn.label}
                  </a>
                ) : (
                  <button key={idx} className={cls} style={style} onClick={btn.onClick}>
                    {btn.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* ── RIGHT ── */}
        <div className="w-full md:w-[48%] flex justify-end mt-5 md:mt-0">
          <div className="relative w-full">

            {image && (
              <div
                className="w-full overflow-hidden flex items-center
                           justify-center md:justify-start
                           h-[410px] sm:h-[430px] lg:h-[450px] pb-[20px] md:pb-[50px]"
              >
                <img
                  src={image}
                  alt={heading}
                  className="w-full h-full object-contain"
                  style={{ transform: 'scale(1.15) translate(30px, 20px)' }}
                />
              </div>
            )}

            {report_count !== undefined && (
              <div
                className="badge-animate absolute z-[2] top-[10px]
                           left-2 scale-[0.65] origin-top-left
                           md:left-0 md:scale-[0.9]
                           lg:-left-[30px] lg:scale-100
                           w-[200px] h-[90px] rounded-lg p-3 cs-reports-badge
                           flex flex-col items-start justify-center text-left
                           hover:scale-[1.08] cursor-default"
              >
                <div className="font-bold text-[28px] leading-tight" style={{ color: 'var(--color-brand-green)' }}>{report_count}</div>
                <div className="text-[15px] font-medium leading-tight mt-0.5">
                Subscriptions Taken</div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default IntroSection;



